import React, {createContext, useContext, useEffect, useState} from 'react';
import {useAuth} from 'react-oidc-context';
import {authenticatedFetch} from '@/services/auth/authenticatedFetch.js';

const defaultFeaturesContext = {
    isFeatureEnabled: () => false,
    loading: false
};

const FeaturesContext = createContext(defaultFeaturesContext);

export const useFeaturesConfig = () => useContext(FeaturesContext);

export const FeaturesProvider = ({children}) => {
    const {user} = useAuth();
    const [features, setFeatures] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        setFeatures({});

        if (!user) {
            setLoading(false);
            return () => {
                active = false;
                controller.abort();
            };
        }

        const fetchFeatures = async () => {
            setLoading(true);
            try {
                const response = await authenticatedFetch(
                    `${window.ENV.BACKEND_URL}/api/secured/feature/config`,
                    { signal: controller.signal }
                );
                if (!response.ok) throw new Error(`Feature request failed (${response.status})`);
                const data = await response.json();
                const featuresMap = data?.features?.reduce((acc, feature) => {
                    acc[feature.name] = feature.enabled;
                    return acc;
                }, {});
                if (active) setFeatures(featuresMap || {});
            } catch (error) {
                if (active && !controller.signal.aborted) {
                    console.error('Failed to fetch features config:', error);
                    setFeatures({});
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        void fetchFeatures();
        return () => {
            active = false;
            controller.abort();
        };
    }, [user?.profile?.sub]);

    const isFeatureEnabled = (featureName) => {
        if (loading) {
            return false;
        }
        if (!features || Object.keys(features).length === 0) {
            return false;
        }
        return features[featureName] === true;
    };

    return (
        <FeaturesContext.Provider value={{isFeatureEnabled, loading}}>
            {children}
        </FeaturesContext.Provider>
    );
};
